import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Accordion, Nav, Table, Badge, Spinner } from 'react-bootstrap';
import {
  ClipboardList,
  MessageCircle,
  ArrowLeft,
  Upload,
  X,
  AlertCircle,
  Loader2,
  Copy,
  Check,
  FileText,
  HelpCircle,
  Mail,
  Phone,
  Users,
  Settings,
  CheckCircle,
  RefreshCw,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

import { useUser } from '../../contexts/UserContext';
import PrivateLayout from '../layouts/PrivateLayout';
import Panel from '../common/Panel';
import Loading from '../common/Loading';
import { useQueryClient } from '@tanstack/react-query';
import { useFetchPublicForms, useSubmitSupportRequest, useFetchUserSubmissions, refreshUserSubmissions } from '../../services/support';

// Icon mapping for dynamic forms
const ICON_MAP = {
  MessageCircle,
  ClipboardList,
  FileText,
  HelpCircle,
  Mail,
  Phone,
  Users,
  Settings,
  AlertCircle,
  CheckCircle,
};

// Format date for display (e.g., "Jan 17, 2026 @ 14:53")
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const datePart = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${datePart} @ ${timePart}`;
};

// Determine if a color is light (for text contrast)
const isLightColor = (hexColor) => {
  if (!hexColor) return false;
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
};

// My Tickets component
const MyTickets = () => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('open');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { data: submissions, isLoading } = useFetchUserSubmissions();

  const openTickets = submissions?.filter((s) => !s.isClosed) || [];
  const closedTickets = submissions?.filter((s) => s.isClosed) || [];
  const displayTickets = activeTab === 'open' ? openTickets : closedTickets;
  const totalCount = submissions?.length || 0;

  // Background refresh on component mount - fetches from Monday and updates DB
  useEffect(() => {
    const doBackgroundRefresh = async () => {
      setIsRefreshing(true);
      try {
        const updated = await refreshUserSubmissions();
        queryClient.setQueryData(['userSupportSubmissions'], updated);
      } catch (err) {
        console.error('Background refresh failed:', err);
      } finally {
        setIsRefreshing(false);
      }
    };

    doBackgroundRefresh();
  }, [queryClient]);

  // Manual refresh handler
  const handleRefresh = async (e) => {
    e.stopPropagation();
    setIsRefreshing(true);
    try {
      const updated = await refreshUserSubmissions();
      queryClient.setQueryData(['userSupportSubmissions'], updated);
    } catch (err) {
      console.error('Refresh failed:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Accordion activeKey={isOpen ? '0' : null} className="mt-4">
      <Accordion.Item eventKey="0">
        <Accordion.Header onClick={() => setIsOpen(!isOpen)}>
          <div className="d-flex align-items-center justify-content-between w-100 pe-3">
            <div className="d-flex align-items-center">
              <span>My Tickets {openTickets.length > 0 && `(${openTickets.length})`}</span>
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="d-flex align-items-center gap-1"
            >
              <RefreshCw size={14} className={isRefreshing ? 'spin' : ''} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </Accordion.Header>
        <Accordion.Body>
          {isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" className="me-2" />
              <span className="text-muted">Loading your tickets...</span>
            </div>
          ) : totalCount === 0 ? (
            <div className="text-center py-4 text-muted">
              <FileText size={32} className="mb-2 opacity-50" />
              <p className="mb-0">You haven't submitted any support tickets yet.</p>
            </div>
          ) : (
            <>
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link
                    active={activeTab === 'open'}
                    onClick={() => setActiveTab('open')}
                    style={{ cursor: 'pointer' }}
                  >
                    Open ({openTickets.length})
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    active={activeTab === 'closed'}
                    onClick={() => setActiveTab('closed')}
                    style={{ cursor: 'pointer' }}
                  >
                    Closed ({closedTickets.length})
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              {displayTickets.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <p className="mb-0">
                    {activeTab === 'open'
                      ? 'No open tickets.'
                      : 'No closed tickets.'}
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead>
                      <tr>
                        <th>Ticket #</th>
                        <th>Form</th>
                        <th>{activeTab === 'closed' ? 'Closed' : 'Submitted'}</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayTickets.map((ticket) => (
                        <tr key={ticket.submissionId}>
                          <td className="fw-medium">
                            {ticket.ticketReference || `#${ticket.mondayItemId}`}
                          </td>
                          <td>{ticket.formTitle || 'Support'}</td>
                          <td>{formatDate(activeTab === 'closed' ? ticket.closedAt : ticket.createdAt)}</td>
                          <td>
                            {ticket.status ? (
                              <span
                                style={{
                                  display: 'inline-block',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.875rem',
                                  fontWeight: 500,
                                  backgroundColor: ticket.statusColor || '#6c757d',
                                  color: isLightColor(ticket.statusColor) ? '#000' : '#fff',
                                }}
                              >
                                {ticket.status}
                              </span>
                            ) : (
                              <Badge bg="secondary">Unknown</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}
        </Accordion.Body>
      </Accordion.Item>
      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Accordion>
  );
};

const SupportTile = ({ icon: Icon, title, description, onClick }) => (
  <Card
    className="h-100 support-tile"
    onClick={onClick}
    style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-4px)';
      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '';
    }}
  >
    <Card.Body className="d-flex align-items-start p-4">
      <div
        className="d-flex align-items-center justify-content-center"
        style={{
          width: '80px',
          minWidth: '80px',
          height: '80px',
          borderRadius: '12px',
          backgroundColor: 'rgba(13, 110, 253, 0.1)',
        }}
      >
        <Icon size={40} className="text-primary" />
      </div>
      <div className="ps-4 pt-1">
        <Card.Title className="mb-1">{title}</Card.Title>
        <Card.Text className="text-muted mb-0">{description}</Card.Text>
      </div>
    </Card.Body>
  </Card>
);

const DynamicSupportForm = ({ form, onBack, user }) => {
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState([]);
  const [ticketId, setTicketId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loadingStage, setLoadingStage] = useState(0);
  const fileInputRef = useRef(null);

  // Loading stage while submitting
  const loadingStages = files.length > 0
    ? [
        { message: 'Creating your support ticket...', duration: 1500 },
        { message: 'Uploading attachments...', duration: null },
      ]
    : [
        { message: 'Creating your support ticket...', duration: null },
      ];

  const copyToClipboard = () => {
    navigator.clipboard.writeText(ticketId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const mutation = useSubmitSupportRequest(
    (data) => {
      setTicketId(data.ticketId);
    },
    (err) => {
      setErrorMessage(err.response?.data?.msg || err.message || 'An unexpected error occurred');
    },
  );

  // Advance loading stages while submitting
  useEffect(() => {
    if (!mutation.isLoading) {
      setLoadingStage(0);
      return;
    }

    const currentStage = loadingStages[loadingStage];
    if (!currentStage?.duration) return; // Last stage, don't advance

    const timeout = setTimeout(() => {
      setLoadingStage((prev) => (prev < loadingStages.length - 1 ? prev + 1 : prev));
    }, currentStage.duration);

    return () => clearTimeout(timeout);
  }, [mutation.isLoading, loadingStage, loadingStages]);

  const handleFieldChange = (fieldKey, value) => {
    setFormData((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate({
      formSlug: form.formSlug,
      formData,
      files,
    });
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...newFiles].slice(0, 5));
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (files.length < 5) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (files.length >= 5) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles((prev) => [...prev, ...droppedFiles].slice(0, 5));
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Success state
  if (ticketId) {
    return (
      <Panel>
        <div className="text-center py-5">
          <div className="mb-4">
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#28a745',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
          </div>
          <h3 className="mb-3">Request Submitted!</h3>
          <p className="text-muted mb-4">Your support request has been received.</p>
          <Alert variant="info" className="d-inline-flex align-items-center gap-2">
            <span><strong>Ticket Reference:</strong> #{ticketId}</span>
            <Button
              variant="link"
              size="sm"
              className="p-0 text-info"
              onClick={copyToClipboard}
              title={copied ? 'Copied!' : 'Copy to clipboard'}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </Button>
          </Alert>
          <div className="mt-4">
            <Button variant="primary" onClick={onBack}>
              Submit Another Request
            </Button>
          </div>
        </div>
      </Panel>
    );
  }

  // Error state
  if (errorMessage) {
    return (
      <Panel>
        <div className="text-center py-5">
          <div className="mb-4">
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#dc3545',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AlertCircle size={40} color="white" />
            </div>
          </div>
          <h3 className="mb-3">Something Went Wrong</h3>
          <p className="text-muted mb-4">
            We couldn't submit your support request. Please try again or contact your support team directly.
          </p>
          <Alert variant="danger" className="text-start mx-auto" style={{ maxWidth: '500px' }}>
            <strong>Error Details:</strong>
            <br />
            <small>{errorMessage}</small>
          </Alert>
          <div className="mt-4">
            <Button variant="outline-secondary" onClick={onBack} className="me-2">
              Go Back
            </Button>
            <Button variant="primary" onClick={() => setErrorMessage(null)}>
              Try Again
            </Button>
          </div>
        </div>
      </Panel>
    );
  }

  // Check if form allows file uploads
  const allowUploads = form.formAllowUploads !== false;

  // Render form field based on type
  const renderField = (field) => {
    const value = formData[field.fieldKey] || '';

    switch (field.fieldType) {
      case 'textarea':
        return (
          <Form.Control
            as="textarea"
            rows={5}
            value={value}
            onChange={(e) => handleFieldChange(field.fieldKey, e.target.value)}
            placeholder={field.fieldPlaceholder}
            required={field.fieldRequired}
          />
        );

      case 'email':
        return (
          <Form.Control
            type="email"
            value={value}
            onChange={(e) => handleFieldChange(field.fieldKey, e.target.value)}
            placeholder={field.fieldPlaceholder}
            required={field.fieldRequired}
          />
        );

      case 'number':
        return (
          <Form.Control
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(field.fieldKey, e.target.value)}
            placeholder={field.fieldPlaceholder}
            required={field.fieldRequired}
          />
        );

      case 'date':
        return (
          <Form.Control
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(field.fieldKey, e.target.value)}
            required={field.fieldRequired}
          />
        );

      case 'select':
        const options = field.fieldOptions || [];
        return (
          <Form.Select
            value={value}
            onChange={(e) => handleFieldChange(field.fieldKey, e.target.value)}
            required={field.fieldRequired}
          >
            <option value="">{field.fieldPlaceholder || 'Select an option...'}</option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        );

      case 'file':
        // File fields are handled separately with the dropzone
        return null;

      default: // text, phone, etc.
        return (
          <Form.Control
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field.fieldKey, e.target.value)}
            placeholder={field.fieldPlaceholder}
            required={field.fieldRequired}
            maxLength={500}
          />
        );
    }
  };

  return (
    <Panel style={{ position: 'relative' }}>
      {/* Loading overlay */}
      {mutation.isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            borderRadius: 'inherit',
          }}
        >
          <Loader2 size={48} className="text-primary" style={{ animation: 'spin 1s linear infinite' }} />
          <p className="mt-3 mb-0 text-muted">{loadingStages[loadingStage]?.message}</p>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      <div className="d-flex align-items-center mb-4">
        <Button variant="link" onClick={onBack} className="p-0 me-2 text-muted">
          <ArrowLeft size={20} />
        </Button>
        <h5 className="mb-0">{form.formTitle} Request</h5>
      </div>

      <Alert variant="secondary" className="mb-4">
        <small>
          <strong>Submitting as:</strong> {user.userFirstName} {user.userLastName} ({user.userEmail})
        </small>
      </Alert>

      <Form onSubmit={handleSubmit}>
        {/* Render dynamic fields */}
        {form.fields?.filter((f) => f.fieldType !== 'file').map((field) => (
          <Form.Group className="mb-3" key={field.fieldKey}>
            <Form.Label>
              {field.fieldLabel}
              {field.fieldRequired && <span className="text-danger"> *</span>}
            </Form.Label>
            {renderField(field)}
          </Form.Group>
        ))}

        {/* File upload section - only show if form allows uploads */}
        {allowUploads && (
          <Form.Group className="mb-4">
            <Form.Label>Attachments (optional, max 5 files)</Form.Label>
            <div
              onClick={() => files.length < 5 && fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              style={{
                border: `2px dashed ${isDragActive ? '#0d6efd' : '#dee2e6'}`,
                borderRadius: '8px',
                padding: '1.5rem',
                textAlign: 'center',
                cursor: files.length >= 5 ? 'not-allowed' : 'pointer',
                backgroundColor: isDragActive ? 'rgba(13, 110, 253, 0.1)' : files.length >= 5 ? '#f8f9fa' : 'transparent',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (files.length < 5 && !isDragActive) {
                  e.currentTarget.style.borderColor = '#0d6efd';
                  e.currentTarget.style.backgroundColor = 'rgba(13, 110, 253, 0.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isDragActive) {
                  e.currentTarget.style.borderColor = '#dee2e6';
                  e.currentTarget.style.backgroundColor = files.length >= 5 ? '#f8f9fa' : 'transparent';
                }
              }}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                multiple
                style={{ display: 'none' }}
                accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx"
              />
              <Upload size={24} className={isDragActive ? 'text-primary mb-2' : 'text-muted mb-2'} />
              <p className={isDragActive ? 'text-primary mb-0' : 'text-muted mb-0'} style={{ fontSize: '0.9rem' }}>
                {files.length >= 5 ? 'Maximum files reached' : isDragActive ? 'Drop files here' : 'Click to upload or drag files here'}
              </p>
            </div>
            {files.length > 0 && (
              <div className="mt-3">
                {files.map((file, index) => (
                  <div
                    key={index}
                    className="d-flex align-items-center justify-content-between bg-light rounded px-3 py-2 mb-2"
                  >
                    <small>{file.name}</small>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-danger"
                      onClick={() => removeFile(index)}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Form.Group>
        )}

        <div className="pt-3 pb-3">
          <Button type="submit" variant="primary" disabled={mutation.isLoading}>
            {mutation.isLoading ? 'Submitting...' : 'Submit Request'}
          </Button>
        </div>
      </Form>
    </Panel>
  );
};

const Support = () => {
  const { user } = useUser();
  const { formSlug: urlFormSlug } = useParams();
  const navigate = useNavigate();
  const [selectedForm, setSelectedForm] = useState(null);

  // Fetch forms from database
  const { data, isLoading, error } = useFetchPublicForms();

  const forms = data?.forms || [];

  // Auto-select form if URL has a slug
  useEffect(() => {
    if (urlFormSlug && forms.length > 0 && !selectedForm) {
      const matchingForm = forms.find((f) => f.formSlug === urlFormSlug);
      if (matchingForm) {
        setSelectedForm(matchingForm);
      }
    }
  }, [urlFormSlug, forms, selectedForm]);

  // Handle going back - clear URL slug if present
  const handleBack = () => {
    setSelectedForm(null);
    if (urlFormSlug) {
      navigate('/support', { replace: true });
    }
  };

  // Check if URL slug doesn't match any form
  const invalidSlug = urlFormSlug && forms.length > 0 && !forms.find((f) => f.formSlug === urlFormSlug);

  if (isLoading) {
    return (
      <PrivateLayout>
        <Container>
          <Loading msg="Loading support options..." />
        </Container>
      </PrivateLayout>
    );
  }

  return (
    <PrivateLayout>
      <Container>
        <h2 className="mb-4">Support</h2>

        {error && (
          <Alert variant="danger" className="mb-4">
            <small>Unable to load support forms. Please try again later.</small>
          </Alert>
        )}

        {!selectedForm ? (
          <>
            {invalidSlug ? (
              <Alert variant="warning">
                <strong>Form not found.</strong> The support form "{urlFormSlug}" does not exist.
                <div className="mt-2">
                  <Button variant="outline-warning" size="sm" onClick={() => navigate('/support', { replace: true })}>
                    View All Support Options
                  </Button>
                </div>
              </Alert>
            ) : forms.length === 0 ? (
              <Alert variant="info">
                No support forms are currently available. Please check back later.
              </Alert>
            ) : (
              <Row className="g-4">
                {forms.map((form) => {
                  const IconComponent = ICON_MAP[form.formIcon] || MessageCircle;
                  return (
                    <Col md={6} key={form.formSlug || form.formId}>
                      <SupportTile
                        icon={IconComponent}
                        title={form.formTitle}
                        description={form.formDescription}
                        onClick={() => setSelectedForm(form)}
                      />
                    </Col>
                  );
                })}
              </Row>
            )}
            {!invalidSlug && <MyTickets />}
          </>
        ) : (
          <DynamicSupportForm
            form={selectedForm}
            onBack={handleBack}
            user={user}
          />
        )}
      </Container>
    </PrivateLayout>
  );
};

export default Support;
